/**
 * @module Permission/Service
 */

import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, ObservableInput, from, of, lastValueFrom } from 'rxjs';
import { catchError, concatAll, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import {
	appPermissions,
	PermissionFunction,
	PermissionsObject,
	PermissionTypesObject,
	PermissionValidationService
} from '@app/services/permissions';
import { Individual, User, SignUpStep } from '@feature/users/shared';
import { ProfileStepsService } from '@feature/profiles/shared';
import { Company, CompanyMemberRef, CompanyMemberRole, CompanyType } from '@feature/companies/shared';
import { AppContextService } from '@app/services/app-context.service';
import { AppContext } from '@app/models/app-context.model';
import { isFunction } from 'rxjs/internal/util/isFunction';
import {Router} from '@angular/router';

/**
 * Service that manages application-level permissions.
 *
 * This service manages which actions user context (individual or business)
 * is allowed to perform, based on their permission role and types.
 *
 * @service
 * @@Injectable
 */
@Injectable({ providedIn: 'root' })
export class PermissionContextService implements OnDestroy {
	/**
	 * BehaviorSubject that holds the available permission types within the application.
	 *
	 * This property is used to manage and emit changes to the permission types, allowing subscribers
	 * to reactively receive updates whenever the permission types change.
	 *
	 * @type {BehaviorSubject<PermissionTypesObject>}
	 * @private
	 * @hidden
	 */
	private _typesSource: BehaviorSubject<PermissionTypesObject>;

	/**
	 * The current context representing either a user or a company member reference.
	 *
	 * This property holds the context information for the currently authenticated user or the
	 * company member, which can be used to determine permissions and roles within the application.
	 *
	 * @type {User | CompanyMemberRef}
	 * @private
	 * @hidden
	 */
	private _currentContext: User | CompanyMemberRef;

	/**
	 * The type of the current context, indicating the role or category of the user.
	 *
	 * This property is used to specify the type of the current context, which can influence
	 * the behavior and permissions available to the user.
	 *
	 * @type {string}
	 * @private
	 * @hidden
	 */
	private _type: string;

	/**
	 * The role associated with the current context.
	 *
	 * This property is used to define the specific role of the user within the application,
	 * which can affect access control and available actions.
	 *
	 * @type {string}
	 * @private
	 * @hidden
	 */
	private _role: string;

	/**
	 * Getter that returns the current context representing either a user or a company member reference.
	 *
	 * This getter retrieves the current context, which can be used to determine the permissions
	 * and roles available to the user within the application.
	 *
	 * @getter
	 * @returns {User | CompanyMemberRef} The current context of the user or company member.
	 */
	get currentContext(): User | CompanyMemberRef {
		return this._currentContext;
	}

	/**
	 * Setter that updates the current context to the specified user or company member reference.
	 *
	 * This setter updates the current context and invokes the method to set the type and role
	 * based on the new context. It allows for dynamic changes in the user's context during
	 * the application's lifecycle.
	 *
	 * @setter
	 * @param {User | CompanyMemberRef} currentContext - The new context to set as the current user or company member.
	 */
	set currentContext(currentContext: User | CompanyMemberRef) {
		this._currentContext = currentContext;
		this._setTypeAndRole();
	}

	/**
	 * Creates an instance of the PermissionContextService.
	 *
	 * @constructor
	 * @param {AppContextService} _contextService - The service that provides the current application context.
	 * @param {ProfileStepsService} _profileStepsService - The service that provides the current profile steps.
	 */
	constructor(
		private _contextService: AppContextService,
		private _profileStepsService: ProfileStepsService,
		private _router: Router
	) {
		this._typesSource = new BehaviorSubject<PermissionTypesObject>({});
		this._contextService.currentContext
			.pipe(filter((ctx: AppContext) => ctx !== null))
			.subscribe((ctx: AppContext) => {
				this.currentContext = ctx?.isPersonalContext ? ctx?.user : ctx?.companyMembership;

				// Declare the permissions whose check vary depending on the context.
				this.addTypes(appPermissions);
			});
	}

	/**
	 * Adds a new type with associated roles to the types source.
	 *
	 * This method updates the current types source by adding a new type and its corresponding
	 * roles. If the `skipObservable` flag is set to true, it will not emit the updated types
	 * through the observable.
	 *
	 * @param {string} type - The name of the type to add.
	 * @param {{ [role: string]: Array<PermissionsObject> }} rolesObj - An object mapping roles to their permissions.
	 * @param {boolean} [skipObservable=false] - Optional flag to skip emitting the updated types through the observable.
	 * @returns {void}
	 */
	public addType(
		type: string,
		rolesObj: { [role: string]: Array<PermissionsObject> },
		skipObservable: boolean = false
	): void {
		const types = {
			...this._typesSource.value,
			[type]: rolesObj
		};
		if (! skipObservable) {
			this._typesSource.next(types);
		}
	}

	/**
	 * Adds multiple types with their associated roles to the types source.
	 *
	 * This method iterates over the provided PermissionTypesObject and adds each type using
	 * the `addType` method. It clears the current types source before adding the new types.
	 *
	 * @param {PermissionTypesObject} typesObj - An object containing types and their associated roles to add.
	 * @returns {void}
	 */
	public addTypes(typesObj: PermissionTypesObject): void {
		this._typesSource.next({});
		Object.keys(typesObj).forEach((type) => {
			this.addType(type, typesObj[type], true);
		});
		this._typesSource.next(typesObj);
	}

	/**
	 * Clears the current types source by emitting an empty object.
	 *
	 * This method resets the types source to an empty state and sets the internal reference
	 * to null, effectively flushing any existing types. This can be useful for resetting
	 * the state of the application or when reinitializing types.
	 *
	 * @returns {void}
	 */
	public flushTypes(): void {
		this._typesSource.next({});
		this._typesSource = null;
	}

	/**
	 * Removes a specific type from the types source based on the provided role name.
	 *
	 * This method creates a copy of the current types, deletes the specified role name from
	 * the copy, and then emits the updated types through the types source observable.
	 *
	 * @param {string} roleName - The name of the role to remove from the types source.
	 * @returns {void}
	 */
	public removeType(roleName: string): void {
		const types = {
			...this._typesSource.value
		};
		delete types[roleName];
		this._typesSource.next(types);
	}

	/**
	 * Retrieves the current types from the types source.
	 *
	 * This method returns the value of the types source, which contains the current mapping
	 * of types and their associated roles. It allows access to the complete set of types
	 * managed by the service.
	 *
	 * @returns {{ [type: string]: { [role: string]: Array<PermissionsObject> } }} The current types from the types source.
	 */
	public getTypes(): { [type: string]: { [role: string]: Array<PermissionsObject> } } {
		return this._typesSource.value;
	}

	/**
	 * Retrieves a specific type based on the provided type name.
	 *
	 * This method checks if the specified type exists in the types source and returns its
	 * associated roles. If the type does not exist, it returns null.
	 *
	 * @param {string} type - The name of the type to retrieve.
	 * @returns {{ [role: string]: Array<PermissionsObject> } | null} The roles associated with the specified type or null if not found.
	 */
	public getType(type: string): {[role: string]: Array<PermissionsObject>} | null {
		return Object.prototype.hasOwnProperty.call(this._typesSource, type) ? this._typesSource.value[type] : null;
	}

	/**
	 * Checks if there are any types present in the types source.
	 *
	 * This method returns a boolean indicating whether the types source contains any entries.
	 *
	 * @returns {boolean} True if there are types present, otherwise false.
	 */
	public hasTypes(): boolean {
		return Object.keys(this._typesSource.value).length > 0;
	}

	/**
     * Checks the given permissions to the current user context (individual or business).
	 *
     * @param {Array<string | PermissionsObject>} permissions - Permissions array to be checked against the current context
     * @param {string} type - Type to be checked against the PermissionTypesObject or current context (optional)
     * @param {Array<string>} roles - Roles array to be checked against the PermissionTypesObject or current context role (optional)
     * @returns Promise<boolean>
     */
	public contextPermissions(
		permissions: Array<string | PermissionsObject>,
		type?: string,
		roles?: Array<string>
	): Promise<boolean> {
		// 1) Check the context type (user or company member relationship.)
		if (this.currentContext instanceof CompanyMemberRef) {
			// 2) Check current context type (customer, agent.)
			const companyType: string = type || this.currentContext.companyType;          // E.g. agent
			const memberRoles: Array<string> = roles || [this.currentContext.memberRole]; // E.g. [viewer, editor]
			return this._hasPermissions(companyType, memberRoles, permissions);
		} else if (this.currentContext instanceof User) {
			// 2) Check current context type (customer, talent.)
			const userType: string = type || ((this.currentContext as User)?.roles != null && (this.currentContext as User)?.roles[0]?.alias);
			const memberRoles: Array<string> = [CompanyMemberRole.owner];
			return this._hasPermissions(userType, memberRoles, permissions);
		}
		return Promise.resolve(false);
	}

	/**
     * Checks the permissions among the user's type and role.
	 *
     * @param {string} type - Type to be checked against the PermissionTypesObject.
     * @param {Array<string>} roles - Roles array to be checked against the PermissionTypesObject.
     * @param {Array<string | PermissionsObject>} permissions - Permissions array to be checked against the specific type and role.
	 * @returns Promise<boolean>
	 *
     * @private
	 * @hidden
     */
	private _hasPermissions(type: string, roles: Array<string>, permissions: Array<string | PermissionsObject>): Promise<boolean> {
		// @note Type is undefined in case the user is new and hasn't picked profile type yet.
		// if (this._typesSource.value[type] == null) return Promise.resolve(false);
		return lastValueFrom(
			from(roles).pipe( // 1) Walk through all the roles to be searched.
				mergeMap((role: string) => { // @link https://bit.ly/3ByBtYT
					let permissionsAsObjects = (permissions as Array<PermissionsObject>);
					let permissionPromises: Array<Observable<boolean>>;

					// @note Ability to accept PermissionsObject array, rather than being stacked to PermissionTypesObject.
					if (permissionsAsObjects != null) {
						permissionPromises = permissionsAsObjects.map((permissionObject: PermissionsObject, i: number) => {
							// Check allows to accept permissions as a string, e.g. *hasPermissions="['canCreatePayment']".
							if (! (permissionObject instanceof Object)) {
								if (Object.prototype.hasOwnProperty.call(PermissionValidationService, permissionObject)) {
									const validatorFunction = PermissionValidationService[permissionObject];
									return this._validatorFunction(validatorFunction);
								}
								return of(false);
							} else if ((permissionObject instanceof Object) && Object.keys(permissionObject)[0]) {
								const validatorFunction = permissionObject[Object.keys(permissionObject)[0]];
								return this._validatorFunction(validatorFunction);
							}
							// Make sure that it's not null.
							return (permissionObject instanceof Object) ? of(!! permissionObject[Object.keys(permissionObject)[0]]) : of(false);
						});
					} else if (this._typesSource.value[type] != null && this._typesSource.value[type][role] && Array.isArray(this._typesSource.value[type][role])) {
						// 2) Map and collect all the permission Observables.
						permissionsAsObjects = (this._typesSource.value[type][role] as Array<PermissionsObject>);
						permissionPromises = permissions.map((permission: string) => {
							const permissionObject = permissionsAsObjects.find((p: any) => Object.prototype.hasOwnProperty.call(p, permission));
							if (permissionObject) {
								const validatorFunction = permissionObject[permission];
								return this._validatorFunction(validatorFunction);
							}
							return of(!! this._typesSource.value[type][role][permission]);
						});
					}

					if (permissionPromises) {
						// @link https://www.learnrxjs.io/learn-rxjs/operators/creation/from
						return from(permissionPromises).pipe(
							concatAll(), // @link https://bit.ly/3kQAWuW
							map((hasPermission: boolean) => hasPermission !== false),
						);
					}
					return of(null);
				})
			)
		);
	}

	/**
	 * Assigns the type and role based on the current context.
	 *
	 * @private
	 * @hidden
	 */
	private _setTypeAndRole(): void {
		// 1) Check the context type (user or company member relationship.)
		if (this.currentContext instanceof CompanyMemberRef) {
			// 2) Check current context type (customer, agent.)
			this._type = (this.currentContext as CompanyMemberRef).companyType;
			this._role = (this.currentContext as CompanyMemberRef).memberRole;
		} else if (this.currentContext instanceof User) {
			// 2) Check current context type (customer, talent.)
			this._type = (this.currentContext as User)?.maxRole?.alias || '';
			this._role = CompanyMemberRole.owner;
		}
	}

	/**
	 * Checks whether the given value is boolean.
	 *
	 * @param value
	 * @returns {value is boolean}
	 *
	 * @private
	 * @hidden
	 */
	private isBoolean(value: any): value is boolean {
		return typeof value === 'boolean';
	}

	/**
	 * Checks whether the current context belongs to a customer or an agent.
	 *
	 * @returns {boolean}
	 */
	public isCustomerOrAgent(): boolean {
		if (this._type == null) return false;
		return (this._type.includes(CompanyType.customer) || this._type.includes(CompanyType.agent));
	}

	/**
	 * Checks whether the current context belongs to a customer.
	 *
	 * @returns {boolean}
	 */
	public isCustomer(): boolean {
		if (this._type == null) return false;
		return this._type.includes(CompanyType.customer);
	}

	/**
	 * Checks whether the current context belongs to an agent.
	 *
	 * @returns {boolean}
	 */
	public isAgent(): boolean {
		if (this._type == null) return false;
		return this._type.includes(CompanyType.agent);
	}

	/**
	 * Checks whether the current context belongs to an individual customer.
	 *
	 * @returns {boolean}
	 */
	public isIndividualCustomer(): boolean {
		return (this.currentContext instanceof User) && (this.currentContext as User)?.isIndividual && (this.currentContext as User).isCustomer;
	}

	/**
	 * Checks whether the current context belongs a user, not a company member.
	 *
	 * @returns {boolean}
	 */
	public isPersonalContext(): boolean { return ! this.isMember(); }

	/**
	 * Checks whether the current context belongs a company member.
	 *
	 * @returns {boolean}
	 */
	public isCompanyContext(): boolean { return this.isMember(); }

	/**
	 * Checks whether the current context belongs to an individual customer.
	 *
	 * @returns {boolean}
	 */
	public isPersonalCustomer(): boolean {
		return (this.currentContext instanceof User)
			&& (this.currentContext as User).isCustomer
			&& this.isPersonalContext();
	}

	/**
	 * Checks whether the current context belongs to a customer company member.
	 *
	 * @returns {boolean}
	 */
	public isCompanyCustomer(): boolean {
		return this.isCompanyContext() && (this.currentContext as CompanyMemberRef).isCustomer;
	}

	/**
	 * Checks whether the current context belongs to an international payer.
	 *
	 * @returns {boolean}
	 */
	public isInternationalCustomer(): boolean {
		if (! this.isPersonalCustomer() && ! this.isCompanyCustomer()) return false;
		const userContext = (this.currentContext instanceof User) ? (this.currentContext as User) : null;
		if (userContext == null) return false;
		return (((this.currentContext instanceof User) // If it's an individual.
			&& (userContext.individual as Individual)?.profile?.address != null // Make sure that address is available (important for user who just picked type).
			&& (userContext.individual as Individual)?.profile?.address?.stateId == null) // If it's not an international individual.
		|| ((this.currentContext instanceof CompanyMemberRef) // If it's a company.
			&& (userContext.company as Company)?.profile?.address != null // Make sure that address is available (important for user who just picked type).
			&& (userContext.company as Company)?.profile?.address?.stateId == null) // If it's not an international company payer.
		);
	}

	/**
	 * Checks whether the current context belongs to an enterprise payer.
	 *
	 * @returns {boolean}
	 */
	public isEnterpriseCustomer(): boolean {
		return this.isCompanyContext()
			&& (this.currentContext as CompanyMemberRef).isCustomer
			&& (this.currentContext as CompanyMemberRef).companyIsEnterprise;
	}

	/**
	 * Checks whether the current context belongs to a talent.
	 *
	 * @returns {boolean}
	 */
	public isTalent(): boolean {
		return (this.currentContext instanceof User) && (this.currentContext as User)?.isTalent;
	}

	/**
	 * Checks whether the current context belongs to a member.
	 *
	 * @returns {boolean}
	 */
	public isMember(): boolean {
		return (this.currentContext instanceof CompanyMemberRef);
	}

	/**
	 * Checks whether the current context has agents.
	 *
	 * @returns {boolean}
	 */
	public isClient(): boolean {
		return this.isTalent() && (this.currentContext as User).agents?.length > 0;
	}

	/**
	 * Checks whether the current context belongs to a new user.
	 *
	 * @returns {boolean}
	 */
	public isNew(): boolean {
		return ((this.currentContext instanceof User) && (this.currentContext as User)?.isNew)
		|| ((this.currentContext instanceof CompanyMemberRef) && (this.currentContext as CompanyMemberRef)?.signupStep != null);
	}

	/**
	 * Checks whether the current context belongs to a super administrator.
	 *
	 * @returns {boolean}
	 */
	public isSuperAdmin(): boolean {
		return (this.currentContext instanceof User) && (this.currentContext as User)?.isAdmin;
	}

	/**
	 * Checks whether the current context has access to the specified profile step.
	 *
	 * @param {string} profileStep
	 * @returns {boolean}
	 */
	public hasAccessToProfileStep(profileStep: string): boolean {
		if (profileStep == null) return false;
		const getStepByName = (this._profileStepsService.getStepByName(profileStep) as SignUpStep);
		// If international customer, check if the current step is among the restricted steps.
		const stepIsRestricted = this.isInternationalCustomer()
			? this._profileStepsService.isStepRestrictedForInternationalCustomer(profileStep)
			: false;

		return (this.currentContext.signupStep == null && ! stepIsRestricted)
			|| (getStepByName != null && (
				(getStepByName.isFinished && ! getStepByName.isHidden)
				|| (this.currentContext.signupStep === getStepByName.alias)
			));
	}

	/**
	 * Checks whether the current context role equals or below the searched role (lower is higher ranked).
	 *
	 * @param {string} matchRole - The role to match.
	 * @param {boolean} strict - Whether the match has to be strict.
	 * @returns {boolean}
	 */
	public roleIsEqualOrBelow(matchRole: string, strict?: boolean): boolean {
		const matchRoleIndex: number = Object.keys(CompanyMemberRole).indexOf(matchRole);
		const contextRoleIndex: number = Object.keys(CompanyMemberRole).indexOf(this._role);
		const match: boolean = (strict === true ? contextRoleIndex === matchRoleIndex : contextRoleIndex <= matchRoleIndex);
		return ((matchRoleIndex !== -1 && contextRoleIndex !== -1) && match);
	}

	/**
	 * Checks whether the current context belongs to the same user, or member.
	 *
	 * @param {number} memberId - Member ID to check.
	 * @returns {boolean}
	 */
	public isSameUser(memberId: number = null): boolean {
		return ((this.currentContext instanceof User) && (this.currentContext as User)?.id === memberId)
        || ((this.currentContext instanceof CompanyMemberRef) && (this.currentContext as CompanyMemberRef)?.memberId === memberId);
	}

	/**
	 * Checks the current router URL
	 *
	 * @returns {string}
	 */
	public getCurrentUrl(): string {
		return this._router.url;
	}

	/**
	 * Runs the permission verification function and present the results.
	 *
	 * @param {PermissionFunction} permissionFunc - The permission function to execute.
	 * @returns {Observable<boolean>}
	 *
	 * @private
	 * @hidden
	 */
	private _validatorFunction(permissionFunc: PermissionFunction): Observable<boolean> {
		// @note Pass 'this' to validatorFunction because it's required in a constructor of PermissionFunction.
		return of(null).pipe( // @link https://www.learnrxjs.io/learn-rxjs/operators/creation/of
			map(() => isFunction(permissionFunc) ? permissionFunc(this) as Promise<boolean> : permissionFunc),
			switchMap((promise: Promise<boolean> | PermissionFunction): ObservableInput<boolean> =>
				this.isBoolean(promise) ? of(promise as boolean) : (promise as Promise<boolean>)
			),
			catchError(() => of(false))
		);
	}

	/**
	 * Lifecycle hook that is called when the service is destroyed.
	 *
	 * This method cleans up the subject by emitting a null value
	 * and unsubscribing from it to prevent memory leaks.
	 *
	 * @returns {void}
	 */
	ngOnDestroy(): void {
		this.flushTypes();
		if (this._contextService.currentContext != null) {
			this._contextService.currentContext.unsubscribe();
		}
	}
}
